import { AxiosError } from "axios";
import { StatusCodes } from "http-status-codes";
import Logger from "./Logger";

/**
 * Standard API error response structure
 */
export interface ApiErrorResponse {
  success: boolean;
  message: string;
  responseObject?: string;
  statusCode: number;
}

/**
 * Structured error information for display
 */
export interface ErrorInfo {
  title: string;
  message: string;
  statusCode: number;
  canRetry: boolean;
  context?: string;
}

/**
 * Error handler configuration
 */
export interface ErrorHandlerConfig {
  showToast?: boolean;
  logError?: boolean;
  context?: string;
}

/**
 * Extract user-friendly error message from API error response
 */
const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiErrorResponse | undefined;

    // Try to get message from response data
    if (data?.message) {
      return data.message;
    }

    // Try to get message from responseObject (sometimes used for error details)
    if (typeof data?.responseObject === "string" && data.responseObject) {
      return data.responseObject;
    }

    // Fallback to axios error message
    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
};

/**
 * Get HTTP status code from error
 */
const getStatusCode = (error: unknown): number => {
  if (error instanceof AxiosError && error.response?.status) {
    return error.response.status;
  }
  return 0;
};

/**
 * Map status code to user-friendly error information
 */
export const mapStatusCodeToErrorInfo = (
  statusCode: number,
  error: unknown,
  context?: string,
): ErrorInfo => {
  const message = extractErrorMessage(error);

  switch (statusCode) {
    case StatusCodes.BAD_REQUEST:
      return {
        title: "Invalid Request",
        message:
          message ||
          "The request contains invalid parameters. Please check your input and try again.",
        statusCode,
        canRetry: true,
        context,
      };

    case StatusCodes.UNAUTHORIZED:
      return {
        title: "Authentication Required",
        message:
          message || "Your session may have expired. Please sign in again.",
        statusCode,
        canRetry: false,
        context,
      };

    case StatusCodes.FORBIDDEN:
      return {
        title: "Access Denied",
        message: message || "You don't have permission to perform this action.",
        statusCode,
        canRetry: false,
        context,
      };

    case StatusCodes.NOT_FOUND:
      return {
        title: "Not Found",
        message: message || "The requested resource could not be found.",
        statusCode,
        canRetry: true,
        context,
      };

    case StatusCodes.CONFLICT:
      return {
        title: "Conflict",
        message:
          message ||
          "The request conflicts with the current state. Please refresh and try again.",
        statusCode,
        canRetry: true,
        context,
      };

    case StatusCodes.UNPROCESSABLE_ENTITY:
      return {
        title: "Validation Error",
        message:
          message ||
          "The data provided could not be processed. Please check your input.",
        statusCode,
        canRetry: true,
        context,
      };

    case StatusCodes.INTERNAL_SERVER_ERROR:
      return {
        title: "Server Error",
        message:
          message ||
          "An internal server error occurred. Please try again later.",
        statusCode,
        canRetry: true,
        context,
      };

    case StatusCodes.BAD_GATEWAY:
    case StatusCodes.SERVICE_UNAVAILABLE:
    case StatusCodes.GATEWAY_TIMEOUT:
      return {
        title: "Service Unavailable",
        message:
          message ||
          "The service is temporarily unavailable. Please try again in a few moments.",
        statusCode,
        canRetry: true,
        context,
      };

    default:
      return {
        title: "Error",
        message: message || "An unexpected error occurred. Please try again.",
        statusCode,
        canRetry: true,
        context,
      };
  }
};

/**
 * Check if error is a network error (no response from server)
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return (
      !error.response &&
      (error.code === "ERR_NETWORK" ||
        error.code === "NETWORK_ERROR" ||
        error.message === "Network Error")
    );
  }
  return false;
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error: unknown): boolean => {
  const statusCode = getStatusCode(error);

  // Network errors are retryable
  if (isNetworkError(error)) {
    return true;
  }

  // 5xx errors are retryable
  if (statusCode >= 500 && statusCode < 600) {
    return true;
  }

  // 408 Request Timeout is retryable
  if (statusCode === StatusCodes.REQUEST_TIMEOUT) {
    return true;
  }

  // 429 Too Many Requests is retryable (with backoff)
  if (statusCode === StatusCodes.TOO_MANY_REQUESTS) {
    return true;
  }

  return false;
};

/**
 * Handle API error with logging and optional toast notification
 * Returns structured error information for display
 */
export const handleApiError = (
  error: unknown,
  config: ErrorHandlerConfig = {},
): ErrorInfo => {
  const { logError = true, context } = config;

  // Log error if enabled
  if (logError) {
    Logger.error(`API Error${context ? ` [${context}]` : ""}`, error, [
      "ApiErrorHandler",
    ]);
  }

  // Handle network errors
  if (isNetworkError(error)) {
    return {
      title: "Connection Error",
      message:
        "Unable to connect to the server. Please check your network connection and try again.",
      statusCode: 0,
      canRetry: true,
      context,
    };
  }

  // Map status code to error info
  const statusCode = getStatusCode(error);
  return mapStatusCodeToErrorInfo(statusCode, error, context);
};

/**
 * Create a standardized error handler for specific contexts
 */
export const createErrorHandler = (defaultContext: string) => {
  return (error: unknown, additionalContext?: string): ErrorInfo => {
    const context = additionalContext
      ? `${defaultContext} - ${additionalContext}`
      : defaultContext;

    return handleApiError(error, { context, logError: true });
  };
};

/**
 * Format error for display in UI components
 */
export const formatErrorForDisplay = (errorInfo: ErrorInfo): string => {
  const parts = [errorInfo.title];

  if (errorInfo.message && errorInfo.message !== errorInfo.title) {
    parts.push(errorInfo.message);
  }

  if (errorInfo.context) {
    parts.push(`Context: ${errorInfo.context}`);
  }

  return parts.join(" - ");
};

/**
 * Utility to safely extract error details for debugging
 */
export const getErrorDetails = (error: unknown): Record<string, unknown> => {
  if (error instanceof AxiosError) {
    return {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };
  }

  return {
    error: String(error),
  };
};
